import { useState, useEffect, useCallback } from 'react';
import { enhancedMatchmakingService, EnhancedMatchingSuggestion, MatchingFilters } from '@/services/enhancedMatchmakingService';
import { withPerformanceLogging } from '@/utils/performanceLogger';
import { Member } from '@/types/member';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface UseIntelligentMatchmakingProps {
  autoRefresh?: boolean;
  refreshInterval?: number;
  limit?: number;
}

interface MatchmakingState {
  suggestions: EnhancedMatchingSuggestion[];
  isLoading: boolean;
  error: string | null;
  filters: MatchingFilters;
}

interface MatchmakingAnalytics {
  totalSuggestions: number;
  averageCompatibility: number;
  topCategories: string[];
  qualityScore: number;
}

export const useIntelligentMatchmaking = ({
  autoRefresh = false,
  refreshInterval = 30 * 60 * 1000, // 30 minutes
  limit = 6
}: UseIntelligentMatchmakingProps = {}) => {
  const { member } = useAuth();
  
  const [state, setState] = useState<MatchmakingState>({
    suggestions: [],
    isLoading: false,
    error: null,
    filters: {}
  });

  const [userFeedback, setUserFeedback] = useState<Map<string, 'like' | 'dislike'>>(new Map());

  const loadSuggestions = useCallback(async (filters?: MatchingFilters) => {
    if (!member) return;

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const performanceAwareFetch = withPerformanceLogging(
        'fetchEnhancedMatchmakingSuggestions',
        enhancedMatchmakingService.fetchEnhancedMatchmakingSuggestions.bind(enhancedMatchmakingService)
      );
      
      const suggestions = await performanceAwareFetch(
        member, 
        filters || state.filters,
        limit
      ) as EnhancedMatchingSuggestion[];

      setState(prev => ({
        ...prev,
        suggestions,
        filters: filters || prev.filters,
        isLoading: false
      }));
    } catch (error) {
      console.error('Error loading intelligent matchmaking suggestions:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Erreur lors du chargement des suggestions',
        isLoading: false
      }));
    }
  }, [member, state.filters, limit]);

  const applyFilters = useCallback(async (newFilters: MatchingFilters) => {
    await loadSuggestions(newFilters);
  }, [loadSuggestions]);

  const clearFilters = useCallback(async () => {
    await loadSuggestions({});
  }, [loadSuggestions]);

  const refreshSuggestions = useCallback(async () => {
    enhancedMatchmakingService.clearPersistentCache();
    await loadSuggestions();
  }, [loadSuggestions]);

  const invalidateCacheForUser = useCallback(() => {
    if (member?.user_id) {
      enhancedMatchmakingService.clearCacheForUser(member.user_id);
    }
  }, [member]);

  const provideFeedback = useCallback(async (
    suggestionId: string, 
    feedback: 'like' | 'dislike',
    memberId?: string
  ) => {
    try {
      setUserFeedback(prev => new Map(prev).set(suggestionId, feedback));

      // Track feedback in database for learning
      if (member?.user_id) {
        const { error } = await supabase
          .from('networking_events')
          .insert({
            user_id: member.user_id,
            event_type: 'matchmaking_feedback',
            target_user_id: memberId,
            metadata: {
              suggestion_id: suggestionId,
              feedback,
              timestamp: new Date().toISOString()
            }
          });

        if (error) {
          console.error('Error saving feedback:', error);
        }
      }

      // Remove disliked suggestions from current view
      if (feedback === 'dislike') {
        setState(prev => ({
          ...prev,
          suggestions: prev.suggestions.filter(s => s.id !== suggestionId)
        }));
      }
    } catch (error) {
      console.error('Error providing feedback:', error);
    }
  }, [member]);

  const getAnalytics = useCallback((): MatchmakingAnalytics => {
    const { suggestions } = state;
    
    if (suggestions.length === 0) {
      return {
        totalSuggestions: 0,
        averageCompatibility: 0,
        topCategories: [],
        qualityScore: 0
      };
    }

    const averageCompatibility = suggestions.reduce((sum, s) => sum + s.compatibility, 0) / suggestions.length;
    
    const categoryCount = new Map<string, number>();
    suggestions.forEach(s => {
      s.categories.forEach(cat => {
        categoryCount.set(cat, (categoryCount.get(cat) || 0) + 1);
      });
    });

    const topCategories = Array.from(categoryCount.entries())
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
      .map(([category]) => category);

    const qualityScore = Math.min(100, Math.round(
      (averageCompatibility + (suggestions.filter(s => s.compatibility > 70).length / suggestions.length) * 50)
    ));

    return {
      totalSuggestions: suggestions.length,
      averageCompatibility: Math.round(averageCompatibility),
      topCategories,
      qualityScore
    };
  }, [state.suggestions]);

  const getSuggestedApproach = useCallback((suggestion: EnhancedMatchingSuggestion): string => {
    const { score_breakdown, categories } = suggestion;
    
    if (score_breakdown.skill_complementarity > 0.7) {
      return "Mentionnez vos compétences complémentaires et proposez une collaboration";
    }
    
    if (score_breakdown.industry_alignment > 0.8) {
      return "Échangez sur les tendances de votre secteur commun";
    }
    
    if (score_breakdown.geographic_proximity > 0.8) {
      return "Proposez un café ou une rencontre locale";
    }
    
    if (categories.includes('Même secteur')) {
      return "Partagez votre expérience dans votre domaine d'expertise commun";
    }
    
    return "Présentez-vous et explorez les opportunités de collaboration";
  }, []);

  // Auto-refresh functionality
  useEffect(() => {
    if (autoRefresh && member) {
      const interval = setInterval(() => {
        loadSuggestions();
      }, refreshInterval);

      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval, member, loadSuggestions]);

  // Real-time listener pour invalidation automatique du cache
  useEffect(() => {
    if (!member?.user_id) return;
    
    console.log('🔊 Real-time listener activé pour connection_requests');
    
    const channel = supabase
      .channel('connection-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'connection_requests',
          filter: `requester_id=eq.${member.user_id},requested_id=eq.${member.user_id}`
        },
        (payload) => {
          console.log('🔄 Connexion modifiée - invalidation cache', payload);
          invalidateCacheForUser();
          loadSuggestions();
        }
      )
      .subscribe();
    
    return () => {
      console.log('🔇 Real-time listener désactivé');
      supabase.removeChannel(channel);
    };
  }, [member?.user_id, invalidateCacheForUser, loadSuggestions]);

  // Initial load
  useEffect(() => {
    if (member) {
      loadSuggestions();
    }
  }, [member]);

  return {
    // State
    suggestions: state.suggestions,
    isLoading: state.isLoading,
    error: state.error,
    filters: state.filters,
    userFeedback,

    // Actions
    applyFilters,
    clearFilters,
    refreshSuggestions,
    provideFeedback,
    invalidateCacheForUser,

    // Analytics
    getAnalytics,
    getSuggestedApproach,

    // Computed values
    hasHighQualitySuggestions: state.suggestions.filter(s => s.compatibility > 80).length > 0,
    suggestionsWithAI: state.suggestions.filter(s => s.aiJustification).length,
    isEmpty: state.suggestions.length === 0 && !state.isLoading,
  };
};